import {
  isOrderFinishRouteStatus,
  TransactionFinalizedSummaryDataQuery$data,
} from '@bts-web/data-layer/server';
import { TradeController } from '../..';
import {
  getOrderById,
  getSavingsPlanDetailsAction,
  getStakeByIdTransaction,
  getTransactionFinalizedSummary,
} from '../../../common/gqlActions';
import { AssetDetailTradeWrapper } from '../../../layouts/AssetDetailTradeWrapper/AssetDetailTradeWrapper';
import { FinishTradeController } from '../FinishTradeController/FinishTradeController.use-server';
import { AssetDetailsControllerWithSuspense } from '../../../asset-details';
import {
  FinishTradeDataType,
  NonNullableStakeTransactionData,
  TradeControllerVariants,
  TradeOperation,
} from '../../utils';
import {
  WithServerSpanHOC,
  rethrowNextErrors,
} from '@bts-web/utils-next-infrastructure';

interface FinishTradeWithDesktopWrapperProps {
  params: Promise<{
    assetId: string;
    operation: TradeControllerVariants;
    offerId?: string;
    offerTransactionStatus?: string;
  }>;
  searchParams: Promise<Record<string, string>>;
}

async function FinishTradeWithDesktopWrapper({
  params,
  searchParams,
}: FinishTradeWithDesktopWrapperProps) {
  const awaitedParams = await params;

  const awaitedSearchParams = await searchParams;

  const { offerId, operation, assetId, offerTransactionStatus } = awaitedParams;

  if (!offerId) {
    throw new Error(`missing offerId`);
  }

  let tradeId: string | undefined;

  let transactionData: FinishTradeDataType | null = null;

  if (['buy', 'sell'].includes(operation)) {
    const response = await getTransactionFinalizedSummary(offerId).catch(
      (err) => {
        rethrowNextErrors(err);

        const errorResponseObject: {
          data: {
            transaction: NonNullable<TransactionFinalizedSummaryDataQuery$data>['transaction'];
          };
        } = {
          data: {
            transaction: {
              id: '',
              __typename:
                operation === 'buy' ? 'BuyTransaction' : 'SellTransaction', // fallback for display to uncertain => In progress state because if the user has reached this page it means that the initial offer has been accepted and processed
              status: 'IN_PROGRESS',
              asset: null,
              assetAmount: null,
              fiatAmount: null,
              taxAmount: null,
              totalAmount: null,
            },
          },
        };

        return errorResponseObject;
      },
    );

    const transaction = response?.data?.transaction ?? null;

    tradeId = transaction?.id;

    const { asset, assetAmount, taxAmount, fiatAmount, totalAmount, status } =
      transaction ?? {};

    if (!transaction) {
      throw new Error(`${operation} transaction not found`);
    }

    transactionData = {
      __typename: transaction.__typename,
      id: transaction.id,
      asset: {
        __typename: asset!.__typename,
        id: asset!.id,
        logoUrl: asset!.logoUrl ?? null,
        name: asset!.name ?? null,
        symbol: asset!.symbol ?? null,
      },
      status: status || 'IN_PROGRESS',
      assetAmount,
      taxAmount,
      fiatAmount,
      totalAmount,
    };
  }

  if (operation === 'savings') {
    const response = await getSavingsPlanDetailsAction(offerId);

    const savingsPlanData = response?.data?.savingsPlans?.edges?.[0]?.node;

    if (!savingsPlanData) {
      throw new Error('Savings plan data not found');
    }

    transactionData = savingsPlanData;
  }

  if (operation === 'stake' || operation === 'unstake') {
    // do not fetch stake transaction details if the transaction has failed because we do not have offerId in that case
    const response =
      offerTransactionStatus !== 'FAILED'
        ? await getStakeByIdTransaction(offerId)
        : null;

    const transaction =
      response?.data?.stakingTransactions?.edges &&
      response?.data?.stakingTransactions?.edges[0]?.node;

    const {
      asset,
      assetAmount,
      fiatAmount,
      status,
      id,
      assetFee,
      createdAt,
      fiatFee,
    } = transaction || {};

    const typename =
      operation === 'stake'
        ? 'StakeStakingTransaction'
        : 'UnstakeStakingTransaction';

    transactionData = {
      __typename: transaction?.__typename || typename,
      asset: {
        __typename: asset?.__typename,
        id: asset?.id || assetId,
        logoUrl: asset?.logoUrl ?? null,
        name: asset?.name ?? null,
        symbol: asset?.symbol ?? null,
      },
      status:
        status || (offerTransactionStatus === 'FAILED' ? 'FAILED' : 'PENDING'),
      assetAmount: assetAmount || null,
      id: id ?? '',
      assetFee,
      createdAt,
      fiatFee,
      fiatAmount: fiatAmount || null,
    } as unknown as NonNullableStakeTransactionData;
  }

  if (operation === TradeOperation.order) {
    const orderResult = await getOrderById(offerId);

    if ('errors' in orderResult) {
      throw new Error(`Order not found for id: ${offerId}`);
    }

    const order = orderResult.data;

    if (!isOrderFinishRouteStatus(offerTransactionStatus)) {
      throw new Error(
        `Invalid order finish route status: ${offerTransactionStatus}`,
      );
    }

    // Finish route encodes outcome; BE order supplies the rest.
    transactionData = {
      ...order,
      status: offerTransactionStatus,
    };
  }

  return (
    <>
      <FinishTradeController
        transactionData={transactionData as FinishTradeDataType}
        searchParams={awaitedSearchParams}
        tradeId={tradeId}
      />

      <AssetDetailTradeWrapper
        hideMobile="tradeElement"
        AssetDetailsContent={
          <AssetDetailsControllerWithSuspense
            assetId={transactionData?.asset?.id || assetId}
            hideTradeCTAFloatingButton
          />
        }
        TradeStateContent={
          <TradeController
            routeParams={awaitedParams}
            searchParams={awaitedSearchParams}
            skipCheckAppropriateness
          />
        }
      />
    </>
  );
}

const FinishTradeWithDesktopWrapperWithSpan = WithServerSpanHOC(
  'trade.finish.render',
  FinishTradeWithDesktopWrapper,
);

export { FinishTradeWithDesktopWrapperWithSpan as FinishTradeWithDesktopWrapper };
